import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import type { FamilyDetail, FamilyListItem } from '../lib/api';
import { conversationsByFamilyAtom } from './chat';

// Types re-exported from the API client so there is one definition
export type { FamilyListItem };

// Type for detailed family view (includes the caller's role from the API)
export type FamilyWithRole = FamilyDetail;

// Persisted atoms
export const currentFamilyIdAtom = atomWithStorage<string | null>(
  'twinden:currentFamilyId',
  null,
  undefined,
  { getOnInit: true }
);

// State atoms
export const familiesAtom = atom<FamilyListItem[]>([]);

export const currentFamilyAtom = atom<FamilyWithRole | null>(null);

export const familiesLoadingAtom = atom<boolean>(false);

// The list has come back at least once, so "no families" is real (D153).
export const familiesLoadedAtom = atom<boolean>(false);

export const familiesErrorAtom = atom<string | null>(null);

// People asking to join, per family I'm a root or admin of (D154 badge).
export const joinRequestCountsAtom = atom<Record<string, number>>({});

// Derived atoms
export const currentFamilyRoleAtom = atom((get) => {
  const families = get(familiesAtom);
  const currentFamilyId = get(currentFamilyIdAtom);

  if (!currentFamilyId) return null;

  const family = families.find((f) => f.id === currentFamilyId);
  return family?.role ?? null;
});

// Roots and admins can manage the family (identity spec §6.1)
export const isCurrentFamilyAdminAtom = atom((get) => {
  const role = get(currentFamilyRoleAtom);
  return role === 'admin' || role === 'root';
});

export const isCurrentFamilyRootAtom = atom((get) => {
  const role = get(currentFamilyRoleAtom);
  return role === 'root';
});

export const hasFamiliesAtom = atom((get) => {
  const families = get(familiesAtom);
  return families.length > 0;
});

// Actions
export const setFamiliesAtom = atom(
  null,
  (_get, set, families: FamilyListItem[]) => {
    set(familiesAtom, families);
    set(familiesLoadedAtom, true);
    set(familiesErrorAtom, null);
  }
);

export const setCurrentFamilyAtom = atom(
  null,
  (_get, set, family: FamilyWithRole | null) => {
    set(currentFamilyAtom, family);
    if (family) {
      set(currentFamilyIdAtom, family.id);
    }
  }
);

export const addFamilyAtom = atom(null, (get, set, family: FamilyListItem) => {
  const families = get(familiesAtom);
  set(familiesAtom, [...families, family]);
});

export const updateFamilyAtom = atom(
  null,
  (
    get,
    set,
    { id, updates }: { id: string; updates: Partial<FamilyListItem> }
  ) => {
    const families = get(familiesAtom);
    set(
      familiesAtom,
      families.map((f) => (f.id === id ? { ...f, ...updates } : f))
    );

    const currentFamily = get(currentFamilyAtom);
    if (currentFamily?.id === id) {
      set(currentFamilyAtom, { ...currentFamily, ...updates });
    }
  }
);

export const removeFamilyAtom = atom(null, (get, set, familyId: string) => {
  const families = get(familiesAtom);
  set(
    familiesAtom,
    families.filter((f) => f.id !== familyId)
  );
  // Its chat list goes with it (M66, R1-1).
  const cached = get(conversationsByFamilyAtom);
  if (familyId in cached) {
    const { [familyId]: _gone, ...rest } = cached;
    set(conversationsByFamilyAtom, rest);
  }

  const currentFamilyId = get(currentFamilyIdAtom);
  if (currentFamilyId === familyId) {
    set(currentFamilyIdAtom, null);
    set(currentFamilyAtom, null);
  }
});

export const clearFamilyStateAtom = atom(null, (_get, set) => {
  set(familiesAtom, []);
  set(currentFamilyAtom, null);
  set(currentFamilyIdAtom, null);
  set(familiesLoadingAtom, false);
  set(familiesErrorAtom, null);
});

// What you call each member of the current family (D143): member id → label.
// Yours alone; loaded by useMemberLabelsSync in the app shell.
export const memberLabelsAtom = atom<Record<string, string>>({});

// A member's name as the app shows it: what I call them (D143), else their
// display name in the current family; null for someone the members don't
// name (yet). Sealed items are named through this from their bound sender
// id (D327): read at render, so a message opened before the members load
// gets its name when they arrive (#113).
export const memberNameAtom = atom((get) => {
  const labels = get(memberLabelsAtom);
  const members = new Map(
    (get(currentFamilyAtom)?.members ?? []).map((member) => [
      member.userId,
      member.user.displayName,
    ])
  );
  return (userId: string): string | null =>
    labels[userId] ?? members.get(userId) ?? null;
});
